"use client";

import {
  spacing,
  fonts,
  fontSizes,
  colors,
  radii,
} from "@softmax/design-system/tokens.stylex";

import * as stylex from "@stylexjs/stylex";
import { styles } from "./agent-handoff.styles";
import type { HandoffCommand } from "./agent-handoff-command";
import { useCopyAcknowledgement } from "../use-copy-acknowledgement";
import { Button } from "@softmax/design-system";
import { TerminalInstructions } from "./terminal-instructions";
import { TerminalCommandLines } from "./terminal-command-lines";

const shells = {
  posix: { name: "Terminal", enter: "Return", prompt: "$" },
  windows: { name: "PowerShell", enter: "Enter", prompt: ">" },
} as const;

export function TerminalBlock({
  command,
  onCopy,
}: Readonly<{
  command: Extract<HandoffCommand, Readonly<{ kind: "terminal" }>>;
  onCopy(text: string): Promise<void>;
}>) {
  const { agent, os, lines } = command;
  const { copied, copy } = useCopyAcknowledgement({
    text: lines.join("\n"),
    onCopy,
  });
  const shell = shells[os];
  return (
    <div {...stylex.props(styles.stack)}>
      <div data-terminal {...stylex.props(localStyles.terminal)}>
        <div {...stylex.props(localStyles.terminalBar)}>
          <span aria-hidden="true" {...stylex.props(localStyles.dots)}>
            <span {...stylex.props(localStyles.dot, localStyles.closeDot)} />
            <span {...stylex.props(localStyles.dot, localStyles.minimizeDot)} />
            <span {...stylex.props(localStyles.dot, localStyles.maximizeDot)} />
          </span>
          <span data-shell-label {...stylex.props(localStyles.shellLabel)}>
            {shell.name}
          </span>
          <Button
            size="sm"
            onClick={() => void copy()}
            aria-label="Copy both lines"
          >
            <span aria-live="polite">{copied ? "Copied" : "Copy"}</span>
          </Button>
        </div>
        <TerminalCommandLines
          lines={lines}
          agent={agent}
          prompt={shell.prompt}
        />
      </div>
      <TerminalInstructions os={os} enter={shell.enter} />
    </div>
  );
}

const localStyles = stylex.create({
  terminal: {
    overflow: "hidden",
    borderRadius: radii.medium,
    backgroundColor: colors.foreground,
  },
  terminalBar: {
    display: "flex",
    alignItems: "center",
    gap: spacing.space12,
    backgroundColor: colors.secondary,
    paddingInline: spacing.space16,
    paddingBlock: spacing.space8,
  },
  dots: { display: "flex", flexShrink: 0, gap: spacing.space6 },
  dot: { width: 11, height: 11, borderRadius: radii.pill },
  closeDot: { backgroundColor: colors.attention },
  minimizeDot: { backgroundColor: colors.action },
  maximizeDot: { backgroundColor: colors.chartGreen },
  shellLabel: {
    minWidth: 0,
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "center",
    fontFamily: fonts.mono,
    fontSize: fontSizes.caption,
    lineHeight: 1.35,
    color: colors.inverse,
  },
});
